import { noop } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import styled, { css } from 'styled-components';
import { injectIntl, FormattedMessage } from 'react-intl';
import { navy, grey25, grey75, white, grey50 } from '@zocdoc/branding/colors';

import {
    NON_INPUT_X_AFFORDANCE_DESKTOP,
    NON_INPUT_X_AFFORDANCE_MOBILE,
} from '../../../../picker-constants';

import { FontFamily } from '../../../../typography';
import { breakpoint } from '../../../../grid';
import { TAB, CARRIER } from '../../../constants';
import Check from '../../../../icons/Check';
import Close from '../../../../icons/Close';

import { LOCALIZED_STRINGS } from './constants';

class BaseHeaderView extends Component {
    static propTypes = {
        carrier: PropTypes.shape({
            id: PropTypes.string,
            name: PropTypes.string,
        }),
        recordChevronAssignment: PropTypes.func,
        showSteps: PropTypes.bool,
        useChevronDropdownHeader: PropTypes.bool,
    };

    static defaultProps = {
        recordChevronAssignment: noop,
        useChevronDropdownHeader: false,
    };

    bcbsStepRef = React.createRef()
    stepsRef = React.createRef()
    carrierStepRef = React.createRef()
    planStepRef = React.createRef()

    render() {
        const {
            showSteps,
            showCarrierAndPlanHeader,
        } = this.props;

        if (!showSteps && !showCarrierAndPlanHeader) {
            return null;
        }

        return (
            <Wrapper
                ref={this.stepsRef}
                data-test="insurance-picker-header"
            >
                {this.maybeRenderCarrierAboveSteps()}
                {this.maybeRenderSteps()}
                {this.maybeRenderCarrierBelowSteps()}
            </Wrapper>
        );
    }

    componentDidMount() {
        const {
            recordChevronAssignment,
            tabChange,
            markTabChange,
        } = this.props;

        recordChevronAssignment();

        if (!tabChange || !this.bcbsStepRef.current) {
            return;
        }

        const totalWidth = this.stepsRef.current.offsetWidth + 10;
        const carrierWidth = this.carrierStepRef.current.offsetWidth;
        const planWidth = this.planStepRef.current.offsetWidth;
        const bcbsWidth = this.bcbsStepRef.current.offsetWidth;
        const totalTabWidth = carrierWidth + planWidth + bcbsWidth;

        if (totalWidth > totalTabWidth) {
            return;
        }

        markTabChange();
    }

    maybeRenderSteps() {
        const {
            showSteps,
            useChevronDropdownHeader,
            planDisabled,
        } = this.props;

        const WrapperComponent = useChevronDropdownHeader ? ChevronStepsWrapper : StepsWrapper;

        if (!showSteps) {
            return;
        }

        return (
            <WrapperComponent
                data-test="insurance-picker-header-steps"
                disabled={planDisabled}
            >
                {this.renderCarrierStep()}
                {this.maybeRenderBcbsStep()}
                {this.renderPlanStep()}
            </WrapperComponent>
        );
    }

    renderCarrierStep() {
        const {
            tab,
            carrier,
            viewInsuranceTab,
            useChevronDropdownHeader,
        } = this.props;
        const selected = tab === TAB.INSURANCE;

        if (useChevronDropdownHeader) {
            return this.renderChevronCarrierStep();
        }
        return (
            <UnderlineStep
                ref={this.carrierStepRef}
                data-test="carrier-step"
                onClick={viewInsuranceTab}
                selected={selected}
            >
                <FormattedMessage {...LOCALIZED_STRINGS.ChooseCarrierStep} />
                {this.renderIcon(carrier)}
            </UnderlineStep>
        );
    }

    renderChevronCarrierStep = () => {
        const {
            carrier,
            viewInsuranceTab,
            tab,
        } = this.props;

        const selected = tab === TAB.INSURANCE;

        return (
            <FirstStep
                data-test="choose-carrier-step"
                isEnd={false}
                isStart={true}
                onClick={viewInsuranceTab}
                selected={selected}
                zIndex="3"
            >
                <FormattedMessage {...LOCALIZED_STRINGS.ChooseCarrier} />
                {this.renderIcon(carrier)}
            </FirstStep>
        );
    }

    maybeRenderBcbsStep() {
        const {
            canSeeBcbs,
            tab,
            planDisabled,
            viewBcbsTab,
            useChevronDropdownHeader,
        } = this.props;
        const selected = tab === TAB.BCBS;

        if (!canSeeBcbs) {
            return;
        }

        if (useChevronDropdownHeader) {
            return this.renderChevronBcbsStep(selected);
        }

        return (
            <UnderlineStep
                ref={this.bcbsStepRef}
                data-test="bcbs-step"
                onClick={viewBcbsTab}
                selected={selected}
            >
                <FormattedMessage {...LOCALIZED_STRINGS.ChooseBcbsStep} />

                {this.renderIcon(!planDisabled)}
            </UnderlineStep>
        );
    }

    renderChevronBcbsStep = selected => {
        const {
            planDisabled,
            viewBcbsTab,
        } = this.props;

        return (
            <MiddleStep
                data-test="choose-bcbs-step"
                onClick={viewBcbsTab}
                selected={selected}
            >
                <FormattedMessage {...LOCALIZED_STRINGS.ChooseBcbs} />
                {this.renderIcon(!planDisabled)}
            </MiddleStep>
        );
    }

    renderPlanStep() {
        const {
            tab,
            planDisabled,
            canSeeBcbs,
            plan,
            useChevronDropdownHeader,
        } = this.props;

        const stepNumber = canSeeBcbs ? '3' : '2';
        const selected = planDisabled ? false : tab === TAB.PLAN;

        if (useChevronDropdownHeader) {
            return this.renderChevronPlanStep();
        }

        const messageProps = {
            ...LOCALIZED_STRINGS.ChoosePlanStep,
            values: {
                stepNumber,
            },
        };

        return (
            <UnderlineStep
                ref={this.planStepRef}
                data-test="plan-step"
                disabled={planDisabled}
                onClick={this.clickPlanTab}
                selected={selected}
            >
                <FormattedMessage {...messageProps} />
                {this.renderIcon(plan)}
            </UnderlineStep>
        );
    }

    renderChevronPlanStep = () => {
        const {
            canSeeBcbs,
            planDisabled,
            tab,
        } = this.props;

        const selected = planDisabled ? false : tab === TAB.PLAN;
        const stepNumber = canSeeBcbs ? '3' : '2';

        const messageProps = {
            ...LOCALIZED_STRINGS.ChoosePlan,
            values: {
                stepNumber,
            },
        };

        return (
            <LastStep
                data-test="choose-plan-step"
                disabled={planDisabled}
                isEnd={true}
                isStart={false}
                onClick={this.clickPlanTab}
                selected={selected}
                zIndex="1"
            >
                <FormattedMessage {...messageProps} />
            </LastStep>
        );
    }

    maybeRenderCarrierAboveSteps = () => {
        const {
            useChevronDropdownHeader,
        } = this.props;

        if (useChevronDropdownHeader) {
            return;
        }

        return this.maybeRenderCarrier(CarrierAboveSteps);
    }

    maybeRenderCarrierBelowSteps = () => {
        const {
            useChevronDropdownHeader,
        } = this.props;

        if (!useChevronDropdownHeader) {
            return;
        }

        const CarrierComponent = this.maybeRenderCarrier(CarrierBelowSteps);
        if (!CarrierComponent) {
            return;
        }

        return (
            <CarrierBelowStepsContainer >
                {CarrierComponent}
                {this.maybeRenderGoBackX()}
            </CarrierBelowStepsContainer>
        );
    };

    maybeRenderGoBackX = () => {
        const {
            tab,
            viewInsuranceTab,
        } = this.props;

        if (tab === TAB.INSURANCE) {
            return;
        }

        return (
            <GoBackX
                data-test="go-back-x"
                onClick={viewInsuranceTab}
            />
        );
    };

    maybeRenderCarrier = CarrierComponent => {
        const {
            carrier,
            showCarrierAndPlanHeader,
        } = this.props;

        if (!carrier || !showCarrierAndPlanHeader) {
            return;
        }

        let { name } = carrier;

        if (carrier.id === CARRIER.BLUE_CROSS_BLUE_SHIELD) {
            name += ' (BCBS)';
        }

        return (
            <CarrierComponent data-test="selected-carrier-name">
                {name}
            </CarrierComponent>
        );
    }

    clickPlanTab = () => {
        const {
            planDisabled,
            viewPlanTab,
        } = this.props;

        if (planDisabled) {
            return;
        }

        viewPlanTab();
    }

    renderIcon(shouldRender) {
        if (!shouldRender) {
            return;
        }

        return (
            <StyledCheck
                data-test="selected-icon"
                height={9.7058823532}
            />
        );
    }
}

const Wrapper = styled.div`
    padding: 16px ${NON_INPUT_X_AFFORDANCE_MOBILE}px 8px;
    position: relative;

    ${breakpoint.small} {
        padding-left: 25px;
        padding-right: 25px;
    }
`;

const Carrier = styled.div`
    font: 18px/24px ${FontFamily.SharpSansSemibold};
    color: ${navy};
`;

const CarrierAboveSteps = styled(Carrier)`
    width: 100%;
    padding: 0 ${NON_INPUT_X_AFFORDANCE_MOBILE}px 8px 0;

    ${breakpoint.small} {
        padding: 4px ${NON_INPUT_X_AFFORDANCE_DESKTOP}px 8px 0;
    }
`;

const CarrierBelowSteps = styled(Carrier)`
    font: 20px/18px ${FontFamily.SharpSansBold};
    width: auto;
    padding: 0px;

    ${breakpoint.small} {
        font: 20px/28px ${FontFamily.SharpSansBold};
    }
`;

const CarrierBelowStepsContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;

    padding: 13px ${NON_INPUT_X_AFFORDANCE_MOBILE}px 3px 0;
    
    ${breakpoint.small} {
        padding: 18px ${NON_INPUT_X_AFFORDANCE_DESKTOP}px 4px 0;
    }
`;

const GoBackX = styled(Close)`
    width: 8px;
    height: 8px;
    margin-left: 4px;
    cursor: pointer;
`;

const StyledCheck = styled(Check)`
    margin-left: 5px;
    position: relative;
    top: 1px;
`;

const Step = styled.div`
    box-sizing: border-box;
    cursor: ${p => p.disabled ? 'cursor' : 'pointer'};
    flex: 1;
    min-width: 160px;
`;

const UnderlineStep = styled(Step)`
    border-bottom: 2px solid ${p => p.selected ? navy : grey25};
    color: ${p => p.disabled ? grey75 : navy};
    font: 13px/18px ${FontFamily.SharpSansSemibold};
    padding: 0 0 8px;
`;

const ColorIfNotSelected = css`${p => p.disabled ? grey50 : navy}`;

const RightArrowMixin = css`
    &:after {
        width: 0px;
        height: 0px;
        content: '';
        position: absolute;

        top: 0px;
        right: -15px;

        border-top: 19px solid transparent;
        border-bottom: 19px solid transparent;
        border-left: 15px solid ${p => p.selected ? navy : white};
    }

    &:before {
        width: 0px;
        height: 0px;
        content: '';
        position: absolute;

        top: -1px;
        right: -16px;

        border-top: 20px solid transparent;
        border-bottom: 20px solid transparent;
        border-left: 16px solid ${navy};
    }
`;

const ChevronStep = styled(Step)`
    margin-left: 0px;
    position: relative;
    background: ${p => p.selected ? navy : white};
    border-top: 1px solid ${p => p.disabled ? grey50 : navy};
    border-bottom: 1px solid ${p => p.disabled ? grey50 : navy};

    height: 40px;
    padding-top: 10px;
    padding-bottom: 10px;

    color: ${p => p.selected ? white : ColorIfNotSelected};
    font: 15px/18px ${FontFamily.SharpSansSemibold};
`;

const FirstStep = styled(ChevronStep)`
    padding-left: 12px;
    z-index: 3;
    border-left: 1px solid ${p => p.disabled ? grey50 : navy};

    ${RightArrowMixin}
`;

const MiddleStep = styled(ChevronStep)`
    padding-left: 31px;
    z-index: 2;

    ${RightArrowMixin}
`;

const LastStep = styled(ChevronStep)`
    padding-left: 31px;
    z-index: 1;
    border-right: 1px solid ${p => p.disabled ? grey50 : navy}; 
`;

const StepsWrapper = styled.div`
    display: flex;

    overflow-x: auto;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
        width: 0 !important;  // remove scrollbar space
        background: transparent;  // optional: just make scrollbar invisible
        display: none;
    }

    white-space: nowrap;

    ${breakpoint.small} {
        overflow-x: inherit;
        width: 100%;
    }
`;

const ChevronStepsWrapper = styled(StepsWrapper)`
    background-color: ${p => p.disabled ? grey50 : navy}
`;

export const HeaderView = injectIntl(BaseHeaderView);
